'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { ApiClientError, type PaywallPayload } from '@/shared/lib/api';
import {
  AnalyticsEvent,
  PackTrigger,
  PaywallSurface,
  trackEvent,
} from '@/shared/lib/analytics';
import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import {
  findPackProduct,
  useStoreCatalog,
} from '@/shared/api/hooks/use-store-catalog';
import { routeLimitErrorToPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import {
  useActivateBuilder,
  useProject,
  useProjects,
} from '@/features/project/hooks/use-projects';
import type { BuilderSlotsMeta } from '@/types/store';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { BadgeCheck, Lock } from 'lucide-react';
import { PackBuyCta } from './PackBuyCta';

/**
 * Facade Pack paywall body — rendered inside the shared `LimitPaywallDialog`
 * whenever the server payload's primary action is `purchase_pack`
 * (PACK_REQUIRED, FREE_DESIGNS_EXHAUSTED). The PACK_*_EXHAUSTED cap codes are
 * info-only now (null primary action) and never reach this body — they fall
 * back to the generic limit layout.
 *
 * All offer copy (title / body / outcome bullets / action labels) comes from
 * the server payload verbatim — the client adds only chrome: the building
 * binding line ("For {building}"), the reference price from GET /v1/store,
 * and the direct-to-app block (web has no purchases; the mobile apps own the
 * buy flow).
 *
 * Builder Pass holders are the one exception that DOES act on web: when the
 * catalog reports `builder_pass.is_owned`, the primary CTA becomes "Activate
 * this building" (spends a monthly slot on the existing subscription — not a
 * purchase), mirroring Apple's PackPaywallSheet. The one-time pack stays as a
 * quiet direct-to-app fallback (e.g. when monthly slots are spent).
 */
export function PackPaywallContent({
  payload,
  projectId,
  onDismiss,
  onCheckoutOpen,
  onCheckoutClose,
}: {
  payload: PaywallPayload;
  /** Building the limit fired on (error meta) or the current route's project. */
  projectId: string | undefined;
  onDismiss: () => void;
  /** Paddle overlay opened — host relaxes its modal lock so it's clickable. */
  onCheckoutOpen?: () => void;
  /** Paddle overlay dismissed (purchase OR cancel) — host re-locks. */
  onCheckoutClose?: () => void;
}) {
  const t = useTranslations('packs');

  // Funnel: the pack paywall surfaced. Once per mount — the dialog remounts
  // per open, so a ref guard is enough.
  const shownLogged = useRef(false);
  useEffect(() => {
    if (shownLogged.current) return;
    shownLogged.current = true;
    trackEvent(AnalyticsEvent.PACK_PAYWALL_SHOWN, {
      surface: PaywallSurface.PACK,
      trigger: PackTrigger.PURCHASE_PACK,
      project_bound: !!projectId,
    });
  }, [projectId]);

  // Funnel: the web "purchase started" analog — a store-badge tap. The actual
  // checkout + verified completion happen in the mobile app (web is read-only),
  // so web never logs *_completed.
  const onStoreTap = () => {
    trackEvent(AnalyticsEvent.PACK_PURCHASE_STARTED, {
      project_bound: !!projectId,
    });
  };

  // Building chrome — the pack is bound to ONE building, so the offer names
  // it. Project detail is normally already cached on generation surfaces;
  // the line simply hides while unknown.
  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const buildingName = projectResponse?.data?.attributes?.name;
  const isPackActive =
    projectResponse?.data?.attributes?.pack?.is_active === true;

  // Building count — read from the already-cached projects list (sidebar loads
  // it; React Query dedupes, no extra fetch). When the user owns MORE THAN ONE
  // building it's unclear which one this Kit attaches to, so the primary
  // purchase action names the bound building ("Get everything for "<name>"").
  // With exactly one building (typical first purchase) we show nothing extra —
  // the plain server-driven store handoff is best. Count unknown → plain (safe).
  const { data: projectsResponse } = useProjects();
  const buildingCount = projectsResponse?.data?.length;
  const multiBuilding = (buildingCount ?? 0) > 1 && !!buildingName;
  const buildingVerbLabel = multiBuilding
    ? t('getEverythingForBuilding', { name: buildingName as string })
    : undefined;

  // Reference price + SKU label from the server catalog. Failure-tolerant:
  // without it the dialog still shows the full server payload — only the
  // price line drops.
  const { data: catalog } = useStoreCatalog({
    projectId,
    enabled: true,
  });
  const product = findPackProduct(catalog);
  const productAttrs = product?.attributes;

  // Builder Pass activation is fully server-driven: the API decides whether
  // activation is the path right now (FREE_DESIGNS_EXHAUSTED with a primary
  // action of `activate_building` — a holder with a free monthly slot) versus
  // selling a one-time kit (`purchase_pack`). The client no longer derives this
  // from catalog ownership + pack state — the server already ran the slot
  // pre-check, so the activate CTA never shows when a slot would be unavailable
  // (the "tap → it fails" anti-pattern). Project binding still needs a project
  // id for the activate endpoint call.
  const canActivateBuilder =
    payload.primary_action?.kind === 'activate_building' && !!projectId;

  const activate = useActivateBuilder();
  const [activatedSlots, setActivatedSlots] = useState<BuilderSlotsMeta | null>(
    null
  );
  /** Inline info shown when slots are spent (server copy, verbatim). */
  const [activationNotice, setActivationNotice] = useState<string | null>(null);
  /** Flips the body into the activation confirmation on success. */
  const [didActivate, setDidActivate] = useState(false);
  /** Flips the body into the purchase celebration on checkout.completed —
   *  staying open as the reward moment, mirroring Apple/Android (which keep the
   *  sheet up on a checkmark celebration rather than vanishing). */
  const [didPurchase, setDidPurchase] = useState(false);
  /** Builder Pass holders see the one-time pack collapsed behind a link — it's
   *  the alternative, not an upsell. Tapping reveals the offer card + buy. */
  const [showOneTimePack, setShowOneTimePack] = useState(false);

  // Reconcile backstop (realtime rule): the success celebration normally flips
  // on Paddle's `checkout.completed` JS event (onPurchased). That event is
  // best-effort — it can be missed (overlay closed early, sandbox flake), which
  // leaves the buyer staring at the same offer card even though the webhook
  // already granted the Kit. The grant lands on the PROJECT resource as
  // `pack.is_active`, refetched here via useProject (invalidated by the WS
  // `pack_purchase` reconcile / the post-checkout poll). When it flips true
  // while this paywall is open, the purchase definitively succeeded — show the
  // celebration regardless of whether the JS event arrived. Builder activation
  // owns its own `didActivate` confirmation, so skip when that path is in play.
  useEffect(() => {
    if (isPackActive && !canActivateBuilder) {
      setDidPurchase(true);
    }
  }, [isPackActive, canActivateBuilder]);

  const onActivate = async () => {
    if (!projectId || activate.isPending) return;
    setActivationNotice(null);
    try {
      const result = await activate.mutateAsync(projectId);
      setActivatedSlots(result.meta?.builder_slots ?? null);
      setDidActivate(true);
    } catch (error) {
      // Monthly slots spent → show the server's info-only copy INLINE; the
      // one-time pack direction below stays as the natural fallback. Routing
      // it to the shared paywall store would dismiss this whole dialog. Any
      // other typed limit (shouldn't happen while is_owned) routes normally.
      if (
        error instanceof ApiClientError &&
        error.code === 'BUILDER_MONTHLY_SLOTS_EXCEEDED'
      ) {
        setActivationNotice(error.getPaywall()?.body ?? error.message);
      } else if (!routeLimitErrorToPaywall(error)) {
        setActivationNotice(
          error instanceof Error ? error.message : String(error)
        );
      }
    }
  };

  // Activation succeeded — confirmation with the server-computed slot counter,
  // mirroring Apple's post-activation celebration. The building's pack is now
  // active (detail cache seeded by the mutation), so the funnel is done.
  if (didActivate) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('builderActivatedTitle')}</DialogTitle>
          {buildingName && (
            <DialogDescription className="text-card-body max-w-[80%]">
              {t('builderActivatedBody', { name: buildingName })}
            </DialogDescription>
          )}
        </DialogHeader>

        {activatedSlots && (
          <p className="text-card-meta text-muted-foreground text-center">
            {t('builderSlotsUsed', {
              used: activatedSlots.used,
              total: activatedSlots.total,
            })}
          </p>
        )}

        <DialogFooter>
          <Button className="w-full" onClick={onDismiss}>
            {t('builderDone')}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // Purchase succeeded — stay open on a checkmark celebration instead of
  // closing, mirroring Apple's `activeCelebration` (and Android). The Kit grant
  // is owned by the webhook; this is the reward-moment UX. The Kit tag flips via
  // the onCompleted refresh/poll already kicked off in usePackCheckout.
  if (didPurchase) {
    return (
      <>
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-screen-subtitle text-center font-bold">
            {payload.product_name ?? productAttrs?.name ?? t('packActiveTitle')}
          </DialogTitle>
          {buildingName && (
            <DialogDescription className="text-card-meta text-muted-foreground text-center">
              {t('packActiveOn', { name: buildingName })}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Same white, borderless, soft-shadowed chrome as the offer card —
            the checkmark + outcome bullets read as the delivered Kit. */}
        <div className="flex w-full flex-col items-center gap-4 rounded-2xl bg-product-card p-6 text-center shadow-xl">
          <BadgeCheck className="text-primary h-10 w-10" aria-hidden="true" />
          {/* Bullets stay left-flagged even though the card centers the
              checkmark: the parent `text-center` would otherwise center the
              multi-line labels (icons left, ragged text), which reads worse
              than the clean left-aligned list of the offer card. */}
          <PaywallBullets bullets={payload.bullets} className="text-left" />
        </div>

        <DialogFooter>
          <Button className="w-full" onClick={onDismiss}>
            {t('builderDone')}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // Offer card — clean white card lifted by a soft shadow (borderless, floating,
  // premium), mirroring the landing's white Facade Pack card; reads as a
  // prepared deliverable, not a cramped "budget" panel. Generous padding so it
  // breathes. Name + reference price head the card; the canonical outcome
  // bullets sit inside it. Without the catalog (still loading / failed) the
  // bullets render standalone so the server-driven value props never disappear
  // with the price. Built once and placed differently per layout (leads for
  // non-subscribers, below the "or" divider for Builder Pass holders).
  const offerCard = productAttrs ? (
    <div className="flex w-full flex-col gap-5 rounded-2xl bg-product-card p-6 shadow-xl">
      {/* Name + price head the card. `flex-wrap` so they sit side-by-side when
          they fit and the price WRAPS onto its own (full, multi-line) line when
          the name + a long currency ("10.000.000 COP") don't — the price must
          NEVER be hidden behind an ellipsis (kills conversion; App Store rejects
          a paywall where the price isn't visible). Mirrors Apple's
          `ViewThatFits` fallback to a stacked VStack. */}
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-1">
          {/* Action-context name drops the storefront "Additional " modifier:
              prefer the payload's plain `product_name` over the catalog name. */}
          {/* Name + price share ONE larger type role (screen-subtitle, bold) so
              the offer reads as a confident hero — mirrors Apple, where both use
              `screenSubtitle`. The price must never ellipsize (App Store rejects
              a hidden price; long currencies wrap onto their own line via the
              parent `flex-wrap`). */}
          <h2 className="min-w-0 text-screen-subtitle text-foreground font-bold">
            {payload.product_name ?? productAttrs.name}
          </h2>
          {/* Server-computed, tax-inclusive total (same "Due today" number the
              Paddle checkout overlay shows) — falls back to the static USD
              reference price only when the country is unknown or the Paddle
              lookup failed. */}
          <p className="text-screen-subtitle text-foreground font-bold">
            {formatPackPrice(productAttrs)}
          </p>
        </div>
        {/* Billing-model promise ("Один объект · Разовая покупка · Без срока
            действия") — server `body`, placed directly under the name + price
            as the next line inside the card (parity with Android). */}
        {payload.body ? (
          <p className="text-card-meta text-muted-foreground">{payload.body}</p>
        ) : null}
      </div>
      <PaywallBullets bullets={payload.bullets} />
    </div>
  ) : (
    <PaywallBullets bullets={payload.bullets} />
  );

  return (
    <>
      {/* Centered, slightly larger headline — mirrors the Apple/Android pack
          sheet (centered, bold, screenSubtitle). `text-balance` spreads long
          copy evenly instead of a ragged stack; the clamp shrinks a full
          sentence one step while short titles stay near the role size (Apple's
          lineLimit(3) + minimumScaleFactor(0.6), Android autoSize). */}
      <DialogHeader className="items-center text-center">
        <DialogTitle className="text-screen-subtitle text-balance text-center font-bold">
          {payload.title}
        </DialogTitle>
        {/* Server-driven contextual note (e.g. FREE_DESIGNS_EXHAUSTED "come
            back tomorrow") — a small secondary line directly under the title,
            verbatim. Mirrors Apple/Android, which place it here (not in a banner
            box) so it reads as a calm subtitle, not an alert. */}
        {payload.note ? (
          <DialogDescription className="text-card-meta text-muted-foreground text-center">
            {payload.note}
          </DialogDescription>
        ) : null}
      </DialogHeader>

      {canActivateBuilder ? (
        // Builder Pass holder: activation IS the primary path (it spends a
        // monthly slot on the existing subscription — no purchase), so it
        // LEADS, mirroring Apple's PackPaywallSheet reorder. The one-time $79
        // pack is the alternative (e.g. monthly slots spent), so the offer card
        // + direct-to-app drop below an "or" divider, de-emphasized.
        <div className="mt-2 flex w-full flex-col gap-3">
          <Button
            className="w-full"
            onClick={onActivate}
            disabled={activate.isPending}
          >
            {activate.isPending
              ? t('builderActivating')
              : (payload.primary_action?.label ?? t('builderActivate'))}
          </Button>
          {activationNotice && (
            <p className="text-card-meta text-muted-foreground text-center">
              {activationNotice}
            </p>
          )}

          {showOneTimePack ? (
            <>
              {/* Centered divider framing the revealed pack as the alternative,
                  not the headline offer (parity with Apple). */}
              <p className="text-card-meta text-muted-foreground mt-1 text-center">
                {t('orGetOneTimePack')}
              </p>

              {offerCard}
              {/* Fallback: a one-time pack instead of spending a slot. Paddle
                  web checkout when configured, else the mobile store handoff. */}
              <PackBuyCta
                projectId={projectId}
                onStoreTap={onStoreTap}
                onLaunch={onCheckoutOpen}
                onPurchased={() => setDidPurchase(true)}
                onClosed={onCheckoutClose}
              />
            </>
          ) : (
            // Collapsed by default: keep the $79 pack as a quiet link so it
            // never reads as an upsell over the Pass the user already holds.
            <button
              type="button"
              onClick={() => setShowOneTimePack(true)}
              className="text-card-meta text-primary mt-1 text-center"
            >
              {t('orGetOneTimePack')}
            </button>
          )}
        </div>
      ) : (
        // Non-subscriber: the pack purchase IS the offer — the card leads and
        // the direct-to-app block is the single primary path (web is read-only,
        // so we direct to the mobile apps instead of a server CTA button).
        <>
          {offerCard}
          {/* `buildingVerbLabel` is set ONLY when the user has >1 building — it
              names the bound building so the multi-building owner knows which
              one this Kit attaches to. With a single building it's undefined and
              the block stays the plain store handoff (no extra verb capsule). */}
          <PackBuyCta
            className="mt-2"
            projectId={projectId}
            onStoreTap={onStoreTap}
            verbLabel={buildingVerbLabel}
            onLaunch={onCheckoutOpen}
            onPurchased={() => setDidPurchase(true)}
            onClosed={onCheckoutClose}
          />
        </>
      )}

      {/* Regional payment restriction (server-driven, e.g. RU/BY where Paddle
          cannot sell at all) — shown above the trust line so the buyer learns
          the restriction BEFORE tapping into a checkout that would dead-end. */}
      {payload.web_payment_notice ? (
        <NoticeBanner
          variant="warning"
          message={payload.web_payment_notice}
        />
      ) : null}

      {/* Trust microcopy — web checks out via Paddle, so it reads the paddle
          twin of the secure-payment line (Apple → App Store, Android → Google
          Play). Server-driven copy. */}
      {payload.secure_payment_paddle ? (
        <p className="text-card-meta text-muted-foreground flex items-center justify-center gap-1">
          <Lock className="h-3 w-3" aria-hidden="true" />
          {payload.secure_payment_paddle}
        </p>
      ) : null}
    </>
  );
}
